// noinspection DuplicatedCode

import {addSeconds, format, parseISO} from 'date-fns';
import HumanReadableTime from '../models/HumanReadableTime';
import {Runner} from '../models/runner';
import {getWaypointDistancesForRun} from '../models/waypoint';
import Tracking from '../tracking';
import RenderProvider from './render';
import initRunnersStore, {RunnersStore} from '../store/runners';
import RunProvider, {LANGUAGE_DE} from './run';
import RunnerStatistics from './runner-statistics';

export default class FavoritesList {
    static instance = null;

    private runProvider: RunProvider;

    private renderProvider: RenderProvider;

    private favoriteRunners: RunnersStore;

    private unsubscribeStore: () => void;

    private readonly rowTemplate: string;

    private runners: Runner[];

    private timeSwitchListener: () => void;

    private onClick: (runner: Runner) => void;

    private distance: number;

    private readonly spinnerElement: string;

    static getInstance(): FavoritesList {
        if (!this.instance) {
            this.instance = new FavoritesList();
        }

        return this.instance;
    }

    constructor() {
        this.runProvider = RunProvider.getInstance();
        this.renderProvider = RenderProvider.getInstance();
        this.favoriteRunners = initRunnersStore();

        this.rowTemplate = this.renderProvider.favoritesRowTemplate?.innerHTML || '';

        if (this.renderProvider.statisticsSpinnerTemplate) {
            this.spinnerElement = this.renderProvider.statisticsSpinnerTemplate.innerHTML;
        }
    }

    subscribe(runners: Runner[], run: string, onClick: (runner: Runner) => void) {
        this.unsubscribe();

        this.onClick = onClick;

        const waypointDistances = getWaypointDistancesForRun(run);
        [this.distance] = waypointDistances;

        const render = (renderRunners: Runner[]) => {
            this.runners = FavoritesList.sortRunnersArray(renderRunners, run, this.distance);
            this.renderFavoritesList(this.distance);
        };

        render(runners);

        this.renderDistanceSegments(waypointDistances, (selectedDistance) => {
            this.distance = selectedDistance;
            render(runners);
        });

        this.unsubscribeStore = this.favoriteRunners.subscribe(({favoriteRunners}) => {
            render(favoriteRunners);
        });

        this.timeSwitchListener = this.handleTimeSwitch.bind(this);
        this.renderProvider.favoritesTimeSwitch.addEventListener('click', this.timeSwitchListener);

        window.removeEventListener('runnerClick', this.handleClickOnRunnerMarker.bind(this));
        window.addEventListener('runnerClick', this.handleClickOnRunnerMarker.bind(this), {once: true});
    }

    handleClickOnRunnerMarker(event: CustomEvent<{startNumber: string}>) {
        event.stopPropagation();
        const runner = this.runners.find(
            ({startNumber}) => startNumber === event.detail.startNumber,
        );
        this.onClick(runner);
    }

    unsubscribe() {
        this.renderProvider.favoritesList.classList.add('hidden');

        if (this.unsubscribeStore) {
            this.unsubscribeStore();
        }

        if (this.timeSwitchListener) {
            this.renderProvider.favoritesTimeSwitch.removeEventListener('click', this.timeSwitchListener);
        }
    }

    handleTimeSwitch(event: Event) {
        event.preventDefault();

        const timeSwitch = this.getTimeSwitch();
        timeSwitch.dataset.status = timeSwitch.dataset.status === 'timeOfDay' ? 'default' : 'timeOfDay';

        this.renderTableHeaders();
        this.renderFavoritesList(this.distance);
    }

    getTimeSwitch(): HTMLButtonElement {
        return this.renderProvider.favoritesTimeSwitch as HTMLButtonElement;
    }

    showTimeOfDay(): boolean {
        return this.getTimeSwitch().dataset.status === 'timeOfDay';
    }

    static escapeRegExp(pattern: string) {
        return pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    renderDistanceSegments(waypointDistances: number[], onSelect: (selectedDistance: number) => void) {
        if (!waypointDistances) return;

        const segments = waypointDistances.map(
            (distance: number, index: number) => this.compileDistanceSegmentItem(
                distance,
                index === waypointDistances.length - 1,
                (selectedDistance: number) => {
                    onSelect(selectedDistance);
                    this.renderDistanceSegments(waypointDistances, onSelect);
                },
            ),
        );

        this.renderProvider.distanceSegmentsContainer.innerHTML = '';
        this.renderProvider.distanceSegmentsContainer.append(...segments);
        this.renderProvider.distanceSegmentsContainer.classList.remove('hidden');
    }

    compileDistanceSegmentItem(
        distance: number,
        isFinish: boolean,
        onSelect: (selectedDistance: number) => void,
    ): Element {
        const item = document.createElement('button');
        item.className = 'race-tracking__filter-button';
        item.innerText = isFinish ? this.runProvider.translate('Ziel') : this.transformDistanceString(distance);

        if (this.distance === distance) {
            item.className += ' race-tracking__filter-button--is-selected';
        }

        item.addEventListener('click', () => {
            item.className += ' race-tracking__filter-button--is-selected';
            onSelect(distance);
        }, {once: true});

        return item;
    }

    renderTableHeaders() {
        const labelsDefault = this.renderProvider.favoritesList.querySelectorAll('.js-status-time-default');
        const labelsTimeOfDay = this.renderProvider.favoritesList.querySelectorAll('.js-status-time-of-day');

        labelsDefault.forEach((label) => {
            if (this.showTimeOfDay()) {
                label.classList.add('hidden');
            } else {
                label.classList.remove('hidden');
            }
        });
        labelsTimeOfDay.forEach((label) => {
            if (this.showTimeOfDay()) {
                label.classList.remove('hidden');
            } else {
                label.classList.add('hidden');
            }
        });
    }

    renderFavoritesList(distance: number) {
        if (!this.renderProvider.favoritesTableBody) return;

        this.renderProvider.favoritesTableBody.innerHTML = '';

        if (!this.runners.length) {
            this.renderProvider.favoritesList.classList.remove('hidden');
            this.renderProvider.searchResults.classList.add('hidden');
            this.renderProvider.searchContainer.classList.add('hidden');
            return;
        }

        const compareRunner = this.runners.find((runner) => {
            return runner.getTimeForDistance(distance).time > 0;
        });

        this.runners.forEach((runner: Runner, index: number) => {
            let deltaTimeString: string = '';
            if (this.showTimeOfDay()) {
                deltaTimeString = runner.eta.time > 0 ? RunnerStatistics.displayTime(runner.eta.time) : '';
            } else if (index !== 0) {
                const deltaTime = FavoritesList.getDifference(distance, runner, compareRunner);
                if (deltaTime.time > 0) {
                    deltaTimeString = RunnerStatistics.displayTime(deltaTime, true, true, true, true);
                }
            }

            let timeString = this.spinnerElement;
            if (this.showTimeOfDay()) {
                if (runner.hasFinished()) {
                    timeString = '🏁';
                } else if (runner.isRunning() && runner.hasResults()) {
                    timeString = runner.eta.time > 0 ? this.displayTimeOfDay(
                        addSeconds(parseISO(runner.startTimestamp), runner.eta.time),
                    ) : this.spinnerElement;
                } else if (runner.hasStarted()) {
                    timeString = this.spinnerElement;
                }
            } else if (runner.getTimeForDistance(distance).time !== 0) {
                timeString = RunnerStatistics.displayTime(runner.getTimeForDistance(distance));
            } else if (runner.isEtaDueForDistance(distance) && !runner.isOutOfTime()) {
                timeString = this.spinnerElement;
            }

            let avatar: HTMLImageElement;
            if (runner.profilePictureUrl) {
                avatar = document.createElement('img');
                avatar.src = runner.profilePictureUrl;
            } else {
                avatar = document.createElement('span') as HTMLImageElement;
                avatar.innerHTML = runner.getInitials();
            }

            let html: string = this.rowTemplate.replace('%deltaTime%', deltaTimeString);
            html = html.replace('%time%', timeString);
            html = html.replace('%name%', runner.getFullName());
            html = html.replace('%avatar%', avatar.outerHTML);
            html = html.replace(
                new RegExp(FavoritesList.escapeRegExp('%startNumber%'), 'g'),
                runner.startNumber,
            );

            this.renderProvider.favoritesTableBody.insertAdjacentHTML('beforeend', html);
        });

        this.renderProvider.favoritesList.querySelectorAll('.js-runner-detail-toggle')
            .forEach((button: HTMLButtonElement) => {
                button.addEventListener('click', () => {
                    const runner = this.runners.find(
                        ({startNumber}) => startNumber === button.dataset.startNumber,
                    );
                    this.onClick(runner);
                }, {once: true});
            });

        this.renderProvider.favoritesList.classList.remove('hidden');

        if (this.runners.length) {
            this.renderProvider.favoritesList.classList.remove('hidden');
            this.renderProvider.searchResults.classList.add('hidden');
            this.renderProvider.searchContainer.classList.add('hidden');

            const {favoriteRunners} = this.favoriteRunners.getState();
            if (Tracking.hasReachedLimit(favoriteRunners)) {
                this.renderProvider.searchToggleButton.classList.add('hidden');
            } else {
                this.renderProvider.searchToggleButton.classList.remove('hidden');
            }
        } else {
            this.renderProvider.favoritesList.classList.add('hidden');
            this.renderProvider.searchToggleButton.classList.remove('hidden');
        }
    }

    static getDifference(distance: number, runner: Runner, compareRunner: Runner): HumanReadableTime {
        if (runner === compareRunner || compareRunner === undefined) {
            return new HumanReadableTime(0);
        }

        const timeChallengeRunner = runner.getTimeForDistance(distance);
        const timeUserRunner = compareRunner.getTimeForDistance(distance);
        if (timeChallengeRunner && timeUserRunner) {
            return new HumanReadableTime(timeChallengeRunner.time - timeUserRunner.time);
        }

        return new HumanReadableTime(0);
    }

    displayTimeOfDay(value: Date): string {
        if (this.runProvider.getLanguage() !== LANGUAGE_DE) {
            return format(value, 'HH:mm');
        }

        return `${format(value, 'HH:mm')}&nbsp;Uhr`;
    }

    static sortRunnersArray(runners: Runner[], run: string, distance: number): Runner[] {
        const sortedRunnersArray = runners.filter((runner) => runner.run === run);

        // Läufer alphabetisch sortieren
        sortedRunnersArray.sort((runnerA, runnerB) => {
            const nameA = `${runnerA.lastName} ${runnerA.firstName}`;
            const nameB = `${runnerB.lastName} ${runnerB.firstName}`;

            if (nameA < nameB) {
                return -1;
            }

            if (nameA > nameB) {
                return 1;
            }

            return 0;
        });

        sortedRunnersArray.sort((runnerA, runnerB) => {
            // Nach Zeiten sortieren
            const timeA = runnerA.getTimeForDistance(distance);
            const timeB = runnerB.getTimeForDistance(distance);

            if (timeA.time > 0 && timeB.time > 0) {
                return timeA.time - timeB.time;
            }

            if (timeA.time > 0) {
                return -1;
            }

            if (timeB.time > 0) {
                return 1;
            }

            // Nach ETA sortieren
            const etaA = runnerA.getEtaForDistance(distance);
            const etaB = runnerB.getEtaForDistance(distance);

            if (etaA.time > 0 && etaB.time > 0) {
                return etaA.time - etaB.time;
            }

            if (etaA.time > 0) {
                return -1;
            }

            if (etaB.time > 0) {
                return 1;
            }

            // Nach bereits gestartet sortieren
            if (runnerA.hasStarted() && runnerB.hasStarted()) {
                return 0;
            }

            if (runnerA.hasStarted()) {
                return -1;
            }

            if (runnerB.hasStarted()) {
                return 1;
            }

            return 0;
        });

        return sortedRunnersArray;
    }

    transformDistanceString(distance: number): string {
        let distanceString: string = distance.toFixed(1)
            .toString();

        if (distanceString.indexOf('.0') !== -1) {
            [distanceString] = distanceString.split('.');
        } else if (this.runProvider.getLanguage() === LANGUAGE_DE) {
            distanceString = distanceString.replace('.', ',');
        }

        return distanceString;
    }
}
